import React from 'react';
import {
  Check,
  Edit2,
  Trash2,
  Plus,
  Minus
} from 'lucide-react';
import { ABLE_COLORS } from '../../../constants';

/**
 * ShoppingItem - Single shopping list item with inline editing
 * 
 * Features:
 * - Circle click to mark as bought (not whole row)
 * - Inline edit form for name, quantity, unit, category
 * - Delete button to remove from list
 */
export default function ShoppingItem({
  item,
  isEditing,
  editName,
  editQtyNumber,
  editQtyUnit,
  editCategory,
  onEditNameChange,
  onEditQtyNumberChange,
  onEditQtyUnitChange,
  onEditCategoryChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onMarkAsBought,
  onRemove,
  onIncrement,
  onDecrement,
  categories,
  categoryConfig,
  unitOptions,
  isLastItem
}) {
  const config = categoryConfig[item.category] || categoryConfig.other;

  // Parse quantity into number for display
  const parseQtyNumber = (qty) => {
    if (!qty) return '1';
    const match = qty.match(/^([\d.]+)/);
    return match ? match[1] : '1';
  };

  return (
    <div
      style={{
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        borderBottom: !isLastItem ? `1px solid ${ABLE_COLORS.borderLight}` : 'none',
        boxSizing: 'border-box',
        width: '100%'
      }}
    >
      {/* Circle - click to mark as bought */}
      <button
        onClick={onMarkAsBought}
        title="Mark as bought"
        style={{
          width: '22px',
          height: '22px',
          borderRadius: '50%',
          border: `2px solid ${ABLE_COLORS.primary}`,
          background: 'none',
          cursor: 'pointer',
          flexShrink: 0,
          padding: 0
        }}
      />

      {/* Inline edit form */}
      {isEditing ? (
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Row 1: Name */}
          <input
            type="text"
            value={editName}
            onChange={(e) => onEditNameChange(e.target.value)}
            placeholder="Item name"
            autoFocus
            style={{
              width: '100%',
              padding: '10px',
              border: `1px solid ${ABLE_COLORS.border}`,
              borderRadius: ABLE_COLORS.radiusSmall,
              fontSize: '16px',
              outline: 'none',
              marginBottom: '8px',
              boxSizing: 'border-box'
            }}
          />
          {/* Row 2: Qty + Unit + Category - wraps on mobile */}
          <div className="shop-edit-row" style={{ display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
            <input
              type="number"
              value={editQtyNumber}
              onChange={(e) => onEditQtyNumberChange(e.target.value)}
              placeholder="Qty"
              style={{
                width: '60px',
                padding: '8px',
                border: `1px solid ${ABLE_COLORS.border}`,
                borderRadius: ABLE_COLORS.radiusSmall,
                fontSize: '16px',
                outline: 'none'
              }}
            />
            <select
              value={editQtyUnit}
              onChange={(e) => onEditQtyUnitChange(e.target.value)}
              style={{
                padding: '8px',
                border: `1px solid ${ABLE_COLORS.border}`,
                borderRadius: ABLE_COLORS.radiusSmall,
                fontSize: '14px',
                outline: 'none',
                background: ABLE_COLORS.neutral
              }}
            >
              {unitOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              value={editCategory}
              onChange={(e) => onEditCategoryChange(e.target.value)}
              className="shop-edit-category"
              style={{
                flex: 1,
                minWidth: '100px',
                padding: '8px',
                border: `1px solid ${categoryConfig[editCategory]?.color}40`,
                borderRadius: ABLE_COLORS.radiusSmall,
                fontSize: '12px',
                fontWeight: '600',
                outline: 'none',
                background: `${categoryConfig[editCategory]?.color}15`,
                color: categoryConfig[editCategory]?.color
              }}
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {categoryConfig[cat]?.label || cat}
                </option>
              ))}
            </select>
          </div>
          {/* Row 3: Save + Cancel */}
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              onClick={onSaveEdit}
              style={{
                flex: 1,
                padding: '10px',
                background: ABLE_COLORS.primary,
                border: 'none',
                borderRadius: ABLE_COLORS.radiusSmall,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                color: 'white',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              <Check size={14} />
              Save
            </button>
            <button
              onClick={onCancelEdit}
              style={{
                padding: '10px 14px',
                background: ABLE_COLORS.bgSubtle,
                border: 'none',
                borderRadius: ABLE_COLORS.radiusSmall,
                cursor: 'pointer',
                color: ABLE_COLORS.textMuted,
                fontWeight: '500',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
          </div>
          <style>{`
            @media (max-width: 360px) {
              .shop-edit-category { flex-basis: 100% !important; }
            }
          `}</style>
        </div>
      ) : (
        <>
          {/* Item name - tap to edit */}
          <div
            onClick={onStartEdit}
            className="shop-item-name"
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: '14px',
              fontWeight: '500',
              color: ABLE_COLORS.textPrimary,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <span style={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap' 
            }}>
              {item.name}
            </span>
            <Edit2 size={10} color={ABLE_COLORS.textMuted} style={{ flexShrink: 0 }} />
          </div>

          {/* Category badge - hidden on very small screens */}
          <div
            className="shop-item-category"
            style={{
              padding: '4px 8px',
              borderRadius: '12px',
              background: `${config.color}15`,
              color: config.color,
              fontSize: '11px',
              fontWeight: '600',
              flexShrink: 0,
              whiteSpace: 'nowrap'
            }}
          >
            {config.label}
          </div>

          {/* Quantity with +/- buttons */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            flexShrink: 0
          }}>
            <button
              onClick={(e) => { e.stopPropagation(); onDecrement(); }}
              title="Decrease quantity"
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '6px',
                border: `1px solid ${ABLE_COLORS.border}`,
                background: ABLE_COLORS.bgSubtle,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0
              }}
            >
              <Minus size={12} color={ABLE_COLORS.textMuted} />
            </button>
            <span style={{
              minWidth: '24px',
              textAlign: 'center',
              fontSize: '14px',
              fontWeight: '600',
              color: ABLE_COLORS.textPrimary
            }}>
              {parseQtyNumber(item.quantity)}
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); onIncrement(); }}
              title="Increase quantity"
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '6px',
                border: `1px solid ${ABLE_COLORS.border}`,
                background: ABLE_COLORS.bgSubtle,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0
              }}
            >
              <Plus size={12} color={ABLE_COLORS.textMuted} />
            </button>
          </div>

          {/* Delete button */}
          <button
            onClick={onRemove}
            title="Remove from list"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0
            }}
          >
            <Trash2 size={16} color={ABLE_COLORS.textMuted} />
          </button>
          
          <style>{`
            @media (max-width: 380px) {
              .shop-item-category { display: none !important; }
            }
          `}</style>
        </>
      )}
    </div>
  );
}
